import * as THREE from "three";

import { compute } from "./compute";
import { shaderMaterialSpecial, shaderMaterial } from "./lines/@material";
import { bakePathsGeometry } from "./lines/@build";
import { v4 as uuidv4 } from "uuid";

import solvers from "./solvers/api";

class Flow {
  constructor(container, opt) {
    this.simId = uuidv4();

    this.params = {
      lineWidth: 0.002,
      numSpheres: 600,
      minimalLineLength: 200,
      lineLengthRandom: 0,
      lineLengthMinimalDrop: 0,
      vaepSmoothSigma: 2.4914,
      fieldSmoothSigma: 2,
      vaepMultiplayer: 90,
      veapSpeedSampleWeight: 0.0134,
      simTraceResolution: 0.002,
    };

    if (opt.params) this.params = { ...this.params, ...opt.params };

    this.componentZ = -0.01;

    const domainWidth = 2 - 0.1; // Domain width
    const domainHeight = 1.2 - 0.1; // Domain height
    const rows = 30;
    const cols = 24;

    this.rows = rows;
    this.cols = cols;

    /*
    for (let i = 0; i < this.cols; i++) {
      for (let j = 0; j < this.rows; j++) {
        const coneMatrix = new THREE.Matrix4();
        const xPos = i * planeWidth - this.domainWidth / 2 + planeWidth / 2;
        const yPos = j * planeHeight - this.domainHeight / 2 + planeHeight / 2;
        coneMatrix
          .makeRotationZ(-Math.PI / 2)
          .setPosition(xPos, yPos, this.componentZ);
        this.coneMesh.setMatrixAt(index, coneMatrix);
        index++;
      }
    }
*/
    this.numSpheres = this.params.numSpheres;

    this.container = container;
    this.domainWidth = domainWidth;
    this.domainHeight = domainHeight;
    this.uvs = [];

    this.initGrid(opt.debugFlow);
  }

  updateParams(params) {
    this.params = { ...this.params, ...params };
    this.numSpheres = this.params.numSpheres;
    //this.initGrid(opt.debugFlow);
    this.initDebugHeatmapGrid(this.currentWeights);
    this.updateField(this.currentFieldData);
  }

  initGrid(debugFlow) {
    const planeWidth = this.domainWidth / this.cols;
    const planeHeight = this.domainHeight / this.rows;
    const coneGeometry = new THREE.ConeGeometry(
      planeWidth / 9,
      planeWidth / 1.5,
      6
    );
    const coneMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });

    this.coneMesh = new THREE.InstancedMesh(
      coneGeometry,
      coneMaterial,
      this.rows * this.cols
    );

    let index = 0;
    for (let i = 0; i < this.cols; i++) {
      for (let j = 0; j < this.rows; j++) {
        const coneMatrix = new THREE.Matrix4();
        const xPos = i * planeWidth - this.domainWidth / 2 + planeWidth / 2;
        const yPos = j * planeHeight - this.domainHeight / 2 + planeHeight / 2;
        coneMatrix
          .makeRotationZ(-Math.PI / 2)
          .setPosition(xPos, yPos, this.componentZ);
        this.coneMesh.setMatrixAt(index, coneMatrix);
        index++;
      }
    }

    this.container.add(this.coneMesh);
    this.coneMesh.visible = false;
  }

  updateWeights(weights) {
    this.currentWeights = weights;
    this.initDebugHeatmapGrid(weights);
  }

  initDebugHeatmapGrid(data) {
    if (!data.length) return;
    let visible = false;

    if (this.planeDebugMesh) {
      this.container.remove(this.planeDebugMesh);
      visible = this.planeDebugMesh.visible;
      this.planeDebugMesh.dispose();
    }

    const planeWidth = this.domainWidth / this.rows;
    const planeHeight = this.domainHeight / this.cols;

    const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

    const planeMesh = new THREE.InstancedMesh(
      planeGeometry,
      new THREE.MeshBasicMaterial(),
      this.rows * this.cols
    );

    var colorArray = new Float32Array(this.rows * this.cols * 3);

    let index = 0;
    for (let i = 0; i < this.cols; i++) {
      for (let j = 0; j < this.rows; j++) {
        const planeMatrix = new THREE.Matrix4();
        const xPos = j * planeWidth - this.domainWidth / 2 + planeWidth / 2;
        const yPos = i * planeHeight - this.domainHeight / 2 + planeHeight / 2;

        planeMatrix.setPosition(xPos, yPos, this.componentZ);
        planeMesh.setMatrixAt(index, planeMatrix);

        const rotatedIndex = index;

        let currentWeight = data[rotatedIndex].passCount;

        // ) *
        // Assign random color or based on some criteria
        /*  const color = new THREE.Color(
          1 * (((this.rows - j) / this.rows) * 0.5),
          0,
          0
        );*/

        const color = new THREE.Color(
          0,
          data[rotatedIndex].veap > 0
            ? data[rotatedIndex].veap * this.params.vaepMultiplayer
            : 0,
          0
        );
        color.toArray(colorArray, index * 3);

        index++;
      }
    }

    colorArray = compute.smooth(
      colorArray,
      this.rows,
      this.cols,
      3,
      this.params.vaepSmoothSigma
    );

    this.forceArray = colorArray;

    planeMesh.instanceMatrix.needsUpdate = true;
    planeMesh.instanceColor = new THREE.InstancedBufferAttribute(colorArray, 3);

    planeMesh.visible = visible;
    this.container.add(planeMesh);
    this.planeDebugMesh = planeMesh;
  }

  updateField(data) {
    if (data.length === 0) return;
    this.currentFieldData = data;

    const planeWidth = this.domainWidth / this.rows;
    const planeHeight = this.domainHeight / this.cols;

    let index = 0;
    for (let i = 0; i < this.rows; i++) {
      for (let j = 0; j < this.cols; j++) {
        const coneMatrix = new THREE.Matrix4();
        const xPos = i * planeWidth - this.domainWidth / 2 + planeWidth / 2;
        const yPos = j * planeHeight - this.domainHeight / 2 + planeHeight / 2;
        const dataPoint = data[index];
        const rotationAngle = Math.atan2(dataPoint.v, dataPoint.u);
        const euler = -Math.PI / 2 + rotationAngle;
        coneMatrix.makeRotationZ(euler).setPosition(xPos, yPos, -0.01);
        this.coneMesh.setMatrixAt(index, coneMatrix);
        index++;
      }
    }

    this.coneMesh.instanceMatrix.needsUpdate = true;
    const velocityData = new Float32Array(this.coneMesh.count * 2);

    for (let i = 0; i < this.coneMesh.count; i++) {
      const matrix = new THREE.Matrix4();
      this.coneMesh.getMatrixAt(i, matrix);
      const rotationAngle = Math.atan2(matrix.elements[4], matrix.elements[0]);
      velocityData[i * 2] = Math.sin(rotationAngle);
      velocityData[i * 2 + 1] = Math.cos(rotationAngle);
    }

    this.uvs = compute.smoothOld(
      velocityData,
      this.cols,
      this.rows,
      this.params.fieldSmoothSigma
    );
  }

  makeLines(particlePaths) {
    this.interleavedBufferGeometry = bakePathsGeometry(
      particlePaths,
      this.params
    );

    if (!this.polylineStripMesh) {
      const polylineStripMesh = new THREE.Mesh(
        this.interleavedBufferGeometry,
        shaderMaterialSpecial
      );
      polylineStripMesh.position.z = 0;
      polylineStripMesh.renderOrder = 0;
      this.container.add(polylineStripMesh);
      this.polylineStripMesh = polylineStripMesh;
    } else {
      if(this.polylineStripMesh.geometry) {
        this.polylineStripMesh.geometry.dispose(); // Dispose the old geometry

      }
      this.polylineStripMesh.geometry = this.interleavedBufferGeometry; // Assign the new geometry
    }
  }

  async updateSpheres() {
    return new Promise(async (resolve) => {
      let simulationProduct = await solvers.run(this.simId, {
        field: this.uvs,
        domainWidth: this.domainWidth,
        domainHeight: this.domainHeight,
        cols: this.cols,
        rows: this.rows,
        forceArray: this.forceArray,
        numSpheres: this.numSpheres,
        params: this.params,
      });
      this.makeLines(simulationProduct.data.result);
    });
  }

  reset(resetFlow) {

    // Clear arrays
    if (resetFlow) this.uvs = [];
    this.spheres = [];
    this.particlePaths = new Array(this.numSpheres).fill(null).map(() => []);

    // Reset cone mesh
    if (this.coneMesh && resetFlow) {
      this.container.remove(this.coneMesh);
      this.coneMesh.geometry.dispose();
      this.coneMesh.material.dispose();
      this.coneMesh = null;
    }

    // Dispose polylineStripMesh if it exists
    if (this.polylineStripMesh) {
      this.container.remove(this.polylineStripMesh);
      this.polylineStripMesh.geometry.dispose();
      this.polylineStripMesh.material.dispose();
      this.polylineStripMesh = null;
    }
  }
}

export { Flow };
